import { useMemo, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { EyeOff, MoreHorizontal, Plus, RotateCcw, Search, Table2, Trash2 } from 'lucide-react';
import { useLiveQuery } from 'dexie-react-hooks';
import { Page } from '../../components/layout/Page.js';
import { DataTable, type Column } from '../../components/data/DataTable.js';
import { Button } from '../../components/ui/button.js';
import { Input } from '../../components/ui/field.js';
import { Badge, Code, EmptyState } from '../../components/ui/primitives.js';
import { Menu, MenuItem } from '../../components/ui/menu.js';
import { ConfirmDialog } from '../../components/ui/dialog.js';
import { ALL_MANAGED_DEFINITIONS } from '../../components/layout/navigation.js';
import { useMasterRows } from '../../db/queries.js';
import { db, type Local } from '../../db/schema.js';
import { deactivateRow, deleteRow, reactivateRow } from '../../db/mutations.js';
import { useCan } from '../../auth/AuthProvider.js';
import { MasterFormDialog } from './MasterFormDialog.js';

type Row = Local<{ id: string; code: string; name: string; isActive: boolean }> &
  Record<string, unknown>;

/**
 * One screen, eleven tables.
 *
 * The alternative - eleven hand-written CRUD screens - is eleven places for the
 * same bug and eleven places to update when the client asks for a column. The
 * registry in navigation.ts is the only thing that differs between them.
 */
export function MasterListPage() {
  const { table: path } = useParams();
  const definition = ALL_MANAGED_DEFINITIONS.find((d) => d.path === path);

  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<string | null | undefined>(undefined);
  const [confirmDelete, setConfirmDelete] = useState<Row | null>(null);
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [confirmBulk, setConfirmBulk] = useState(false);

  // Each definition names its own resource: the eleven catalog tables share
  // 'catalog-masters', while locations, stores and checkpoints are governed
  // separately, so the screen has to ask about the table it is actually showing.
  const resource = definition?.resource ?? 'catalog-masters';
  const canRead = useCan('read', resource);
  const canWrite = useCan('create', resource);
  const canDelete = useCan('delete', resource);

  const rows = useMasterRows(definition?.table ?? 'companies') as Row[] | undefined;

  // Usage counts: a master row referenced by a mould must be deactivated, not
  // deleted, or the mould loses its spec. The UI needs to know before offering.
  const usage = useLiveQuery(async () => {
    if (!definition) return {};
    const moulds = await db.moulds.toArray();
    const counts: Record<string, number> = {};
    const fkFields = FK_BY_TABLE[definition.table] ?? [];
    for (const m of moulds) {
      for (const fk of fkFields) {
        const id = (m as unknown as Record<string, string | null>)[fk];
        if (id) counts[id] = (counts[id] ?? 0) + 1;
      }
    }
    return counts;
  }, [definition?.table]);

  const filtered = useMemo(() => {
    if (!rows) return rows;
    const needle = search.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((r) =>
      `${r.code} ${r.name}`.toLowerCase().includes(needle),
    );
  }, [rows, search]);

  /**
   * The selection, split by what may safely be done to each row.
   *
   * A master row a mould points at cannot be deleted without taking that
   * mould's specification with it - the single-row menu has always known this,
   * and doing it in bulk is exactly where it would go unnoticed. So the
   * selection is separated here and the bar offers delete only for the part
   * that is genuinely unused, saying plainly how many are held back and why.
   * Deactivating is the answer for the rest: the value stops being offered on
   * the form and every mould already carrying it keeps it.
   */
  const chosen = useMemo(() => (rows ?? []).filter((r) => selected.has(r.id)), [rows, selected]);
  const inUse = useMemo(() => chosen.filter((r) => (usage?.[r.id] ?? 0) > 0), [chosen, usage]);
  const deletable = useMemo(() => chosen.filter((r) => (usage?.[r.id] ?? 0) === 0), [chosen, usage]);
  const activeChosen = useMemo(() => chosen.filter((r) => r.isActive !== false), [chosen]);
  const inactiveChosen = useMemo(() => chosen.filter((r) => r.isActive === false), [chosen]);

  /** Run one mutation across the selection, then drop it. */
  async function bulk(run: (row: Row) => Promise<unknown>, list: Row[]) {
    setBusy(true);
    try {
      for (const row of list) await run(row);
      setSelected(new Set());
      setConfirmBulk(false);
    } finally {
      setBusy(false);
    }
  }

  const columns = useMemo<Array<Column<Row>>>(() => {
    const base: Array<Column<Row>> = [
      {
        key: 'code',
        header: 'Code',
        primary: true,
        sortValue: (r) => r.code,
        render: (r) => (
          <div className="flex items-center gap-2">
            <Code className="font-semibold">{r.code}</Code>
            {r._pending === 1 ? (
              <span className="size-1.5 rounded-full bg-warning" aria-label="Not yet synced" />
            ) : null}
            <span className="truncate lg:hidden">{r.name}</span>
          </div>
        ),
      },
      {
        key: 'name',
        header: 'Name',
        hideOnMobile: true,
        sortValue: (r) => r.name,
        render: (r) => <span className="font-medium">{r.name}</span>,
      },
    ];

    for (const col of definition?.extraColumns ?? []) {
      base.push({
        key: col.key,
        header: col.label,
        sortValue: (r) => (r[col.key] as string | number | null) ?? null,
        render: (r) => {
          const v = r[col.key];
          return v === null || v === undefined || v === '' ? (
            <span className="text-subtle-foreground">-</span>
          ) : (
            <span className="tabular">{String(v)}</span>
          );
        },
      });
    }

    base.push({
      key: 'usage',
      header: 'Used by',
      sortValue: (r) => usage?.[r.id] ?? 0,
      render: (r) => {
        const count = usage?.[r.id] ?? 0;
        return count ? (
          <span className="tabular text-muted-foreground">{count} moulds</span>
        ) : (
          <span className="text-subtle-foreground">-</span>
        );
      },
    });

    base.push({
      key: 'status',
      header: 'Status',
      sortValue: (r) => (r.isActive === false ? 1 : 0),
      render: (r) =>
        r.isActive === false ? <Badge tone="neutral">Inactive</Badge> : <Badge tone="success">Active</Badge>,
    });

    return base;
  }, [definition, usage]);

  if (!definition) return <Navigate to="/" replace />;
  if (!canRead) return <Navigate to="/" replace />;

  return (
    <Page
      title={definition.title}
      description={definition.description}
      actions={
        canWrite ? (
          <Button variant="primary" onClick={() => setEditing(null)}>
            <Plus className="size-4" /> New {definition.singular.toLowerCase()}
          </Button>
        ) : null
      }
      toolbar={
        <div className="relative max-w-sm">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={`Search ${definition.title.toLowerCase()}…`}
            className="pl-9"
            type="search"
            aria-label={`Search ${definition.title}`}
          />
        </div>
      }
    >
      {selected.size > 0 ? (
        <div className="mb-3 flex flex-wrap items-center gap-2 rounded-xl border border-primary bg-primary-subtle/40 px-3 py-2">
          <span className="text-sm font-medium">{selected.size} selected</span>

          {canWrite && activeChosen.length > 0 ? (
            <Button
              size="sm"
              variant="secondary"
              disabled={busy}
              onClick={() => void bulk((r) => deactivateRow(definition.table, r.id), activeChosen)}
            >
              <EyeOff className="size-3.5" /> Deactivate {activeChosen.length}
            </Button>
          ) : null}

          {canWrite && inactiveChosen.length > 0 ? (
            <Button
              size="sm"
              variant="secondary"
              disabled={busy}
              onClick={() => void bulk((r) => reactivateRow(definition.table, r.id), inactiveChosen)}
            >
              <RotateCcw className="size-3.5" /> Reactivate {inactiveChosen.length}
            </Button>
          ) : null}

          {canDelete && deletable.length > 0 ? (
            <Button size="sm" variant="ghost" disabled={busy} onClick={() => setConfirmBulk(true)}>
              <Trash2 className="size-3.5" /> Delete {deletable.length}
            </Button>
          ) : null}

          {/* Said here rather than discovered after pressing delete. */}
          {inUse.length > 0 ? (
            <span className="text-sm text-muted-foreground">
              {inUse.length} in use by moulds - deactivate {inUse.length === 1 ? 'it' : 'them'}{' '}
              instead
            </span>
          ) : null}

          <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>
            Clear
          </Button>
        </div>
      ) : null}

      <DataTable
        rows={filtered}
        columns={columns}
        selection={canWrite ? { selected, onChange: setSelected } : undefined}
        rowKey={(r) => r.id}
        onRowClick={canWrite ? (r) => setEditing(r.id) : undefined}
        initialSort={{ key: 'code', direction: 'asc' }}
        rowActions={
          canWrite
            ? (r) => (
                <Menu
                  trigger={
                    <Button variant="ghost" size="icon" aria-label={`Actions for ${r.code}`}>
                      <MoreHorizontal className="size-4" />
                    </Button>
                  }
                >
                  <MenuItem onSelect={() => setEditing(r.id)}>Edit</MenuItem>
                  {r.isActive === false ? (
                    <MenuItem onSelect={() => void reactivateRow(definition.table, r.id)}>
                      Reactivate
                    </MenuItem>
                  ) : (
                    <MenuItem onSelect={() => void deactivateRow(definition.table, r.id)}>
                      Deactivate
                    </MenuItem>
                  )}
                  {canDelete && !(usage?.[r.id] ?? 0) ? (
                    <MenuItem destructive onSelect={() => setConfirmDelete(r)}>
                      Delete
                    </MenuItem>
                  ) : null}
                </Menu>
              )
            : undefined
        }
        empty={
          search ? (
            <EmptyState
              icon={<Search />}
              title="Nothing matches"
              description={`No ${definition.title.toLowerCase()} contain "${search}".`}
              action={
                <Button variant="secondary" onClick={() => setSearch('')}>
                  Clear search
                </Button>
              }
            />
          ) : (
            <EmptyState
              icon={<Table2 />}
              title={`No ${definition.title.toLowerCase()} yet`}
              description={definition.description}
              action={
                canWrite ? (
                  <Button variant="primary" onClick={() => setEditing(null)}>
                    <Plus className="size-4" /> New {definition.singular.toLowerCase()}
                  </Button>
                ) : null
              }
            />
          )
        }
      />

      <MasterFormDialog
        definition={definition}
        open={editing !== undefined}
        onOpenChange={(open) => !open && setEditing(undefined)}
        rowId={editing ?? null}
      />

      <ConfirmDialog
        open={!!confirmDelete}
        onOpenChange={(open) => !open && setConfirmDelete(null)}
        destructive
        title={`Delete ${confirmDelete?.code}?`}
        description="Nothing references it, so it can be removed permanently. Deactivate instead if you might need it later."
        confirmLabel="Delete"
        loading={busy}
        onConfirm={() => {
          if (!confirmDelete) return;
          setBusy(true);
          void deleteRow(definition.table, confirmDelete.id)
            .then(() => setConfirmDelete(null))
            .finally(() => setBusy(false));
        }}
      />

      <ConfirmDialog
        open={confirmBulk}
        onOpenChange={setConfirmBulk}
        destructive
        title={`Delete ${deletable.length} ${deletable.length === 1 ? definition.singular.toLowerCase() : definition.title.toLowerCase()}?`}
        description={
          <>
            <p>
              None of these is referenced by a mould, so they can be removed permanently.
              Deactivate instead if you might need them later.
            </p>
            {/* Named, not counted. Deleting the wrong twelve rows is not
                something anyone spots from a number alone. */}
            <ul className="mt-2 max-h-40 overflow-y-auto">
              {deletable.slice(0, 12).map((r) => (
                <li key={r.id} className="flex gap-2">
                  <Code className="text-xs">{r.code}</Code>
                  <span className="truncate">{r.name}</span>
                </li>
              ))}
              {deletable.length > 12 ? <li>and {deletable.length - 12} more</li> : null}
            </ul>
          </>
        }
        confirmLabel={`Delete ${deletable.length}`}
        loading={busy}
        onConfirm={() => void bulk((r) => deleteRow(definition.table, r.id), deletable)}
      />
    </Page>
  );
}

/** Which mould columns point at each master table - drives the usage count. */
const FK_BY_TABLE: Record<string, string[]> = {
  companies: ['ownerCompanyId', 'manufactureCompanyId'],
  projects: ['projectId'],
  designers: ['designerId'],
  toolTypes: ['toolTypeId'],
  runnerTypes: ['runnerTypeId'],
  gateTypes: ['gateTypeId'],
  ejectionTypes: ['ejectionTypeId'],
  materials: ['coreCavityMaterialId', 'moldBaseMaterialId'],
  materialGrades: ['materialGradeId'],
  mechanismTypes: ['mechanismTypeId'],
  nameplateTemplates: ['nameplateTemplateId'],
};
